import React, { useState } from "react";
import { FONTS, COLORS } from "./animauxTagsData";
import { ICON_OPTIONS, ICON_CATEGORIES, IconPreview } from "./icons";
import RatingChip from "./RatingChip";
import FAQSection from "./FAQSection";

const inputStyle = {
  background: "var(--input-bg)",
  border: "1px solid var(--input-border)",
  color: "var(--text)",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  fontFamily: "'Montserrat', sans-serif",
  width: "100%",
  padding: "14px 16px",
  borderRadius: "10px",
  fontSize: "14px",
  boxSizing: "border-box",
};

const onFocus = (e) => {
  e.target.style.borderColor = "#D4AF37";
  e.target.style.boxShadow = "0 0 0 1px rgba(212,175,55,0.25)";
};
const onBlur = (e) => {
  e.target.style.borderColor = "var(--input-border)";
  e.target.style.boxShadow = "none";
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: "800",
  letterSpacing: "0.25em",
  color: "var(--label-color)",
  textTransform: "uppercase",
  margin: "0 0 12px 0",
};

const divider = (
  <div style={{ height: "1px", background: "var(--divider)", margin: "6px 0 24px" }} />
);

// Canadian phone mask
function formatPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const DesignPicker = ({ design, setDesign, lang, labelStyle }) => {
  const designs = Array.from({ length: 42 }, (_, i) => i + 1);
  const selectedNum = design.replace("design-", "");
  const [zoomedDesign, setZoomedDesign] = useState(null);

  return (
    <div className="cfg-block">
      <div className="design-picker-header">
        <p style={labelStyle}>
          {lang === "fr" ? "6. Modèle de Gravure" : lang === "es" ? "6. Modelo de Grabado" : "6. Engraving Design"}
        </p>
        {design !== "none" && (
          <div className="selected-design-chip">
            {lang === "fr" ? "Sélectionné" : lang === "es" ? "Seleccionado" : "Selected"}: <strong>#{selectedNum}</strong>
          </div>
        )}
      </div>

      <div className="design-grid">
        <div
          className={`design-item ${design === "none" ? "active" : ""}`}
          onClick={() => setDesign("none")}
        >
          <div className="design-none-icon">✕</div>
          <div className="design-tag">{lang === "fr" ? "Aucun" : lang === "es" ? "Ninguno" : "None"}</div>
        </div>
        {designs.map((num) => (
          <div
            key={num}
            className={`design-item ${design === `design-${num}` ? "active" : ""}`}
            onClick={() => {
              setDesign(`design-${num}`);
              setZoomedDesign(num);
            }}
          >
            <img src={`/img/redondas/designs/animaux-design-${num}.webp`} alt={`Design ${num}`} />
            <div className="design-tag">#{num}</div>
            <div className="design-zoom-hint">🔍</div>
          </div>
        ))}
      </div>

      {/* Zoom Modal for Designs */}
      {zoomedDesign && (
        <div className="design-zoom-modal" onClick={() => setZoomedDesign(null)}>
          <div className="design-zoom-content" onClick={(e) => e.stopPropagation()}>
            <button className="design-zoom-close" onClick={() => setZoomedDesign(null)}>✕</button>
            <img src={`/img/redondas/designs/animaux-design-${zoomedDesign}.webp`} alt={`Design ${zoomedDesign}`} />
            <div className="design-zoom-footer">
              Design <strong>#{zoomedDesign}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default function ConfiguratorPanel({
  t,
  lang,
  model,
  setModel,
  color,
  setColor,
  font,
  setFont,
  petName,
  setPetName,
  phone,
  setPhone,
  addr,
  setAddr,
  extra,
  setExtra,
  design,
  setDesign,
  qty,
  setQty,
  icon,
  setIcon,
  unitPrice,
  itemTotal,
  onAddToCart,
  setScreen,
}) {
  const [justAdded, setJustAdded] = useState(false);
  const instrItems = Array.isArray(t.instr_items) ? t.instr_items : [];
  const NAME_MAX = 12;

  const handleAddToCart = () => {
    onAddToCart && onAddToCart();
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2200);
  };

  const colorList = model === "redonda"
    ? [
        { name: "plata", hex: "#cfd1d1", label: "Silver" },
        { name: "oro", hex: "#d8b45a", label: "Gold" },
      ]
    : model === "gatos"
      ? COLORS.filter((c) => c.name.startsWith("gato_"))
      : COLORS.filter((c) => c.name !== "oro" && !c.name.startsWith("gato_"));

  return (
    <div data-testid="configurator-panel" className="config-panel-inner">
      <div className="config-panel-pad">
        <div data-testid="instructions-box" className="instructions-box">
          <div className="instructions-head">
            <h3>{t.instr_title}</h3>
            <RatingChip rating={4.9} count={127} label={t.reviews_total} compact />
          </div>
          <ul>
            {instrItems.map((item, i) => (
              <li key={item}>
                <span className="instr-num">{i + 1}.</span>
                <span className="instr-text">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="cfg-step-divider">{t.model_label}</div>
        <div data-testid="model-selector" className="cfg-block">
          <div className="model-grid">
            {[
              { key: "hueso", label: t.model_bone },
              { key: "patas", label: t.model_paw },
              { key: "redonda", label: t.model_round },
              { key: "gatos", label: t.model_cat || "Chats" },
            ].map((m) => (
              <button
                key={m.key}
                data-testid={`model-btn-${m.key}`}
                onClick={() => setModel(m.key)}
                className={`model-btn ${model === m.key ? "active" : ""}`}
                aria-pressed={model === m.key}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>


        {divider}
        <div className="cfg-step-divider">{t.front_label}</div>
        <div data-testid="front-engraving" className="cfg-block">
          <p style={labelStyle}>{t.front_label}</p>
          <input
            value={petName}
            onChange={(e) => setPetName(e.target.value.slice(0, NAME_MAX))}
            placeholder={t.name_ph}
            maxLength={NAME_MAX}
            data-testid="input-front-text"
            aria-label={t.name_ph}
            style={{ ...inputStyle, fontFamily: font }}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          <div className="char-counter" aria-live="polite">
            {(petName || "").length}/{NAME_MAX} {t.char_counter}
          </div>
        </div>

        <div data-testid="back-engraving" className="cfg-block">
          <p style={labelStyle}>{t.back_label}</p>

          {/* --- INICIO: Renderizado Condicional de Ejemplos de Texto --- */}
          {model === "redonda" && (
            <img 
              src="/img/ejemplos-texto.jpg" 
              alt="Ejemplos de texto para grabar" 
              style={{ width: "100%", borderRadius: "8px", marginBottom: "15px" }} 
            />
          )}
          {/* --- FIN: Renderizado Condicional --- */}

          <div className="back-inputs">
            <input
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder={t.phone_ph}
              maxLength={12}
              inputMode="tel"
              data-testid="input-phone"
              aria-label={t.phone_ph}
              style={inputStyle}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            <input
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              placeholder={t.addr_ph}
              maxLength={30}
              data-testid="input-addr"
              aria-label={t.addr_ph}
              style={inputStyle}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            <input
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder={t.extra_ph}
              maxLength={30}
              data-testid="input-extra"
              aria-label={t.extra_ph}
              style={inputStyle}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          </div>
        </div>
        
        {divider}

        <div className="cfg-step-divider">{t.color_label || "Style"}</div>
        <div data-testid="color-selector" className="cfg-block">
          <p style={labelStyle}>{model === "redonda" ? t.finish_label : t.color_label}</p>
          <div className="color-row">
            {colorList.map((c) => (
              <button
                key={c.name}
                data-testid={`color-btn-${c.name}`}
                onClick={() => setColor(c.name)}
                title={c.label}
                aria-label={`Couleur ${c.label}`}
                aria-pressed={color === c.name}
                className={`color-dot-btn ${color === c.name ? "active" : ""}`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        </div>

        {model === "gatos" && (
          <div className="cfg-cat-info">
            <strong>{t.cat_price_label || "8.99$ CAD"}</strong>
            <span>{t.material_cat}</span>
            <button type="button" onClick={() => setScreen && setScreen("catalog")} className="cfg-cat-btn">
              {t.nav_catalog || "Catalogue"}
            </button>
          </div>
        )}

        {/* --- Galería de diseños de referencia para medallas redondas --- */}
        {model === "redonda" && (
          <div className="cfg-block">
            <p style={labelStyle}>
              {lang === "fr" ? "Designs de référence" : lang === "es" ? "Diseños de referencia" : "Design References"}
            </p>
            <div className="redonda-gallery-wrap">
              <img
                src="/img/redondas/disenos-redondas.png"
                alt="Diseños de medallas redondas disponibles"
                className="redonda-gallery-img"
              />
              <p className="redonda-gallery-caption">
                {lang === "fr"
                  ? "✨ Styles personnalisables — gravure laser précise"
                  : lang === "es"
                  ? "✨ Estilos personalizables — grabado láser preciso"
                  : "✨ Customizable styles — precise laser engraving"}
              </p>
            </div>
          </div>
        )}

        {/* Free decorative icon — engaging hook */}
        <div data-testid="icon-selector" className="cfg-block">
          <div className="icon-label-row">
            <p style={labelStyle}>{t.icon_label || "Icône"}</p>
            <span className="free-badge">{t.icon_free_badge || "INCLUS GRATUIT"}</span>
          </div>
          <p className="cfg-help">{t.icon_help}</p>

          <IconPicker
            t={t}
            lang={lang}
            value={icon}
            onChange={setIcon}
          />
        </div>

        {/* Font selector with live previews */}
        <div data-testid="font-selector" className="cfg-block">
          <p style={labelStyle}>{t.font_label}</p>
          <div className="font-grid">
            {FONTS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFont(f.value)}
                className={`font-btn ${font === f.value ? "active" : ""}`}
                aria-pressed={font === f.value}
                data-testid={`font-btn-${f.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className="font-preview" style={{ fontFamily: f.value }}>
                  {(petName || "Aa Bb Cc").slice(0, 8)}
                </span>
                <small>{f.label}</small>
              </button>
            ))}
          </div>
        </div>

        {/* Design Picker (Round Only) --- */}
        {model === "redonda" && (
          <DesignPicker
            design={design}
            setDesign={setDesign}
            lang={lang}
            labelStyle={labelStyle}
          />
        )}

        {/* Benefits Checklist & Social Proof */}
        <div className="sales-machine-container">
          <div className="social-proof-badge">
            <span className="pulse-icon">●</span>
            {lang === "fr" ? "28 personnes ont commandé aujourd'hui" : lang === "es" ? "28 personas han comprado hoy" : "28 people ordered today"}
          </div>

          <div className="benefits-checklist">
            <div className="benefit-item">
              <span className="check">✓</span>
              <span>{lang === "fr" ? "Acier 316L (Inoxydable)" : lang === "es" ? "Acero 316L (Inoxidable)" : "316L Surgical Steel"}</span>
            </div>
            <div className="benefit-item">
              <span className="check">✓</span>
              <span>{lang === "fr" ? "Gravure Laser Permanente" : lang === "es" ? "Grabado Láser Permanente" : "Permanent Laser Engraving"}</span>
            </div>
            <div className="benefit-item">
              <span className="check">✓</span>
              <span>{lang === "fr" ? "Hypoallergénique & Léger" : lang === "es" ? "Hipoalergénico & Ligero" : "Hypoallergenic & Light"}</span>
            </div>
          </div>
        </div>

        {divider}

        <QuantitySection t={t} lang={lang} qty={qty} setQty={setQty} unitPrice={unitPrice} itemTotal={itemTotal} />

        <button
          type="button"
          onClick={handleAddToCart}
          className="add-to-cart-btn"
          data-testid="add-to-cart-btn"
          disabled={justAdded}
        >
          {justAdded ? (t.added_to_cart || "Ajouté ✓") : (t.add_to_cart || "Ajouter au panier")}
        </button>

        <div className="trust-signals">
          <div className="secure-payment-line">
            <span className="lock-icon">🔒</span>
            {lang === "fr" ? "Paiement sécurisé via Stripe & PayPal" : lang === "es" ? "Pago seguro vía Stripe & PayPal" : "Secure payment via Stripe & PayPal"}
          </div>
          <div className="payment-badges">
            <div className="badge-item">VISA</div>
            <div className="badge-item">MASTERCARD</div>
            <div className="badge-item">AMEX</div>
            <div className="badge-item">PAYPAL</div>
            <div className="badge-item">APPLE PAY</div>
          </div>
          <p className="shipping-guarantee">
            ✅ {lang === "fr" ? "LIVRAISON GRATUITE CANADA & USA" : lang === "es" ? "ENVÍO GRATIS CANADÁ & USA" : "FREE SHIPPING CANADA & USA"}
          </p>
        </div>

        <div style={{ height: "1px", background: "var(--divider)", margin: "32px 0" }} />
        <FAQSection t={t} />

        <div className="cfg-footer">
          <p>{t.social_follow}</p>
          <div>
            <a href="https://www.facebook.com/animauxtags/" target="_blank" rel="noopener noreferrer">Facebook</a>
            <a href="https://www.instagram.com/animauxtags" target="_blank" rel="noopener noreferrer">Instagram</a>
          </div>
          <p className="cfg-copy">© 2026 ANIMAUX TAGS.</p>
        </div>
      </div>
    </div>
  );
}

function IconPicker({ t, lang, value, onChange }) {
  const [activeCategory, setActiveCategory] = useState("animaux");
  const noneOption = ICON_OPTIONS.find((o) => o.key === "none");
  const categoryIcons = ICON_OPTIONS.filter((o) => o.category === activeCategory);

  return (
    <div data-testid="icon-picker">
      {/* Category tabs */}
      <div className="icon-cat-tabs" role="tablist" aria-label="Categories d'icônes">
        {ICON_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            role="tab"
            aria-selected={activeCategory === cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`icon-cat-tab ${activeCategory === cat.key ? "active" : ""}`}
            data-testid={`icon-cat-${cat.key}`}
          >
            {cat[lang] || cat.fr}
          </button>
        ))}
      </div>

      {/* Always-visible "Aucun" + active category icons */}
      <div className="icon-grid">
        <button
          key={noneOption.key}
          type="button"
          onClick={() => onChange(noneOption.key)}
          className={`icon-btn ${value === noneOption.key ? "active" : ""}`}
          aria-pressed={value === noneOption.key}
          aria-label={noneOption[lang] || noneOption.fr}
          data-testid={`icon-btn-${noneOption.key}`}
        >
          <IconPreview kind="none" color={value === noneOption.key ? "#0A0A0A" : "var(--text)"} />
          <small>{noneOption[lang] || noneOption.fr}</small>
        </button>
        {categoryIcons.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`icon-btn ${value === opt.key ? "active" : ""}`}
            aria-pressed={value === opt.key}
            aria-label={opt[lang] || opt.fr}
            data-testid={`icon-btn-${opt.key}`}
          >
            <IconPreview kind={opt.key} color={value === opt.key ? "#0A0A0A" : "var(--text)"} />
            <small>{opt[lang] || opt.fr}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuantitySection({ t, lang, qty, setQty, unitPrice, itemTotal }) {
  return (
    <div data-testid="quantity-section" className="qty-block">
      <div className="bundle-offer-card">
        <div className="bundle-badge">OFFRE LIMITÉE</div>
        <div className="bundle-content">
          <p className="bundle-title">
            {lang === "fr" ? "Achetez 2, Économisez 15%" : lang === "es" ? "Compra 2, Ahorra 15%" : "Buy 2, Save 15%"}
          </p>
          <p className="bundle-sub">
            {lang === "fr" ? "Ajoutez una deuxième médaille pour un ami !" : lang === "es" ? "¡Añade una segunda medalla para un amigo!" : "Add a second tag for a friend!"}
          </p>
        </div>
        <div className="bundle-icon">🎁</div>
      </div>

      <div className="qty-row">
        <div className="qty-left">
          <span className="qty-label">{t.qty_label}</span>
          <div className="qty-controls">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="−" data-testid="qty-minus">−</button>
            <span data-testid="qty-value">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} aria-label="+" data-testid="qty-plus">+</button>
          </div>
        </div>

        <div className="qty-right">
          <div className="qty-total-label">{t.total_label}</div>
          <div className="qty-total-value" data-testid="qty-total">${itemTotal.toFixed(2)}</div>
          <div className="qty-unit">${unitPrice.toFixed(2)} CAD / unit</div>
        </div>
      </div>

      {qty >= 2 && (
        <div className="qty-discount-badge-active">
          {qty === 2 ? t.discount_2 : t.discount_3}
        </div>
      )}
    </div>
  );
}